import { useState, useEffect, useRef, useCallback } from 'react'
import { Terminal } from '@xterm/xterm'
import { AttachAddon } from '@xterm/addon-attach'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import { PageHeader, colors } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { MANAGED_SERVICES } from '@alpha-edge/shared'

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

export function ContainerShellPage() {
  const [selectedContainer, setSelectedContainer] = useState<string>(MANAGED_SERVICES[0])
  const [state, setState] = useState<ConnectionState>('disconnected')
  const [error, setError] = useState<string | null>(null)
  const termRef = useRef<HTMLDivElement>(null)
  const terminalRef = useRef<Terminal | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const fitRef = useRef<FitAddon | null>(null)

  const cleanup = useCallback(() => {
    wsRef.current = null
    if (terminalRef.current) {
      terminalRef.current.dispose()
      terminalRef.current = null
    }
    fitRef.current = null
    setState('disconnected')
  }, [])

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000)
    }
    cleanup()
  }, [cleanup])

  const connect = useCallback(() => {
    if (wsRef.current) return

    const token = getAuthToken()
    if (!token) {
      setError('Not authenticated')
      setState('error')
      return
    }

    setState('connecting')
    setError(null)

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/api/admin/containers/${selectedContainer}/shell?token=${encodeURIComponent(token)}`
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setState('connected')

      const terminal = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: 'Menlo, Monaco, Consolas, monospace',
        theme: {
          background: '#1a1b26',
          foreground: '#a9b1d6',
          cursor: '#c0caf5',
        },
      })
      terminalRef.current = terminal

      const fit = new FitAddon()
      fitRef.current = fit
      terminal.loadAddon(fit)

      if (termRef.current) {
        terminal.open(termRef.current)
        fit.fit()
      }

      const attach = new AttachAddon(ws)
      terminal.loadAddon(attach)

      terminal.onResize(({ cols, rows }) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'resize', cols, rows }))
        }
      })

      setTimeout(() => fit.fit(), 100)
    }

    ws.onclose = (ev) => {
      cleanup()
      if (ev.code === 4429) {
        setError('Session limit reached (max 2 concurrent shells)')
      } else if (ev.code === 4400) {
        setError('Invalid container name')
      }
    }

    ws.onerror = () => {
      setError('Connection failed')
      setState('error')
      cleanup()
    }
  }, [selectedContainer, cleanup])

  useEffect(() => {
    function handleResize() {
      fitRef.current?.fit()
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      disconnect()
    }
  }, [disconnect])

  return (
    <div>
      <PageHeader title="Container Shell" subtitle="Interactive terminal session into managed containers" />

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
        <select
          value={selectedContainer}
          onChange={(e) => setSelectedContainer(e.target.value)}
          disabled={state === 'connected'}
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: `1px solid ${colors.border}`,
            background: colors.bgCard,
            color: colors.text,
            fontSize: '0.875rem',
          }}
        >
          {MANAGED_SERVICES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        {state === 'disconnected' || state === 'error' ? (
          <button onClick={connect} style={btnStyle}>
            Connect
          </button>
        ) : (
          <button onClick={disconnect} style={{ ...btnStyle, background: '#dc2626' }}>
            Disconnect
          </button>
        )}

        <span style={{ fontSize: '0.8rem', color: stateColor(state) }}>{state}</span>
      </div>

      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#991b1b',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      <div
        ref={termRef}
        style={{
          height: '500px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: `1px solid ${colors.border}`,
          background: '#1a1b26',
        }}
      />
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  background: '#2563eb',
  color: '#fff',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500,
}

function stateColor(state: ConnectionState): string {
  switch (state) {
    case 'connected':
      return '#16a34a'
    case 'connecting':
      return '#f59e0b'
    case 'error':
      return '#dc2626'
    default:
      return colors.textMuted
  }
}
